import React from 'react';

import Typography from '@mui/material/Typography';
import SettingsIcon from '@mui/icons-material/Settings';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';

import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';

import AccountCircle from '@mui/icons-material/AccountCircle';
import MailIcon from '@mui/icons-material/Mail';
import Group from '@mui/icons-material/Group';
import InfoIcon from '@mui/icons-material/Info';
import AttributionIcon from '@mui/icons-material/Attribution';

import PersonAddIcon from '@mui/icons-material/PersonAdd';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Fab from '@mui/material/Fab';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';

import { AppState } from '../states/appState';
import { SettingState, User } from '../states/settingState';
import { SettingActions } from '../containers/SettingContainer';

import { UserUpdateDialog } from './UserUpdateDialog';
import { UserAdditionDialog } from './UserAdditionDialog';
import { UserGroupsUpdatePanel } from './UserGroupsUpdatePanel';
import { CatalogUpdatePanel } from './CatalogUpdatePanel';
import { FieldUpdatePanel } from './FieldUpdatePanel';
import { ActTypeUpdatePanel } from './ActTypeUpdatePanel';

type SettingProps = AppState & SettingState & SettingActions;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const USER_STATUS = {
  'EXTERNAL_PROVIDER': 'ユーザーはサードパーティの IdP でサインインしました。',
  'UNKNOWN': 'ユーザーのステータスは不明です。',
  'RESET_REQUIRED': 'ユーザーは確認されていますが、サインインする前にコードを要求し、パスワードをリセットする必要があります。',
  'FORCE_CHANGE_PASSWORD': 'ユーザーは確認されており、一時パスワードを使用してサインインできますが、最初のサインイン時に、ユーザーは他の操作を行う前にパスワードを新しい値に変更する必要があります。',
  'DELETED': 'cognitoから削除されています。',
  'CONFIRMED': 'ユーザーは確認されています。',
};

export const ROLE_NAME = {
  'Manager': '管理者',
  'Approver': '承認者',
  'Editor': '編集者',
  'Viewer': '閲覧者',
};

const ROW_PER_PATE = 20;

const INIT_USER = {
  userId: '',
  name: '',
  eMail: '',
  role: '',
  userGroupId: '',
  userGroupName: '',
  userStatus: '',
  enabled: true,
};

export interface AdditionUser {
  id: number;
  name: string;
  eMail: string;
  role: string;
  userGroupId: string;
  userGroupName: string;
  notificationFlg: boolean;
  commited: boolean;
};

const INIT_ADDITION_USER: AdditionUser = {
  id: 1,
  name: '',
  eMail: '',
  role: 'Viewer',
  userGroupId: '',
  userGroupName: '',
  notificationFlg: false,
  commited: false,
};

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

export const Setting: React.FC<SettingProps> = (props) => {

  // パネル切替用
  const [value, setValue] = React.useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 1) {
      props.setLoading(true);
      props.getUserGroupsStateAsync(props.accountid, props.idid);
    } else if (newValue === 2) {
      props.setLoading(true);
      props.getCatalogStateAsync(props.accountid, props.idid);
      props.getCatalogAsync(props.accountid, props.idid);
    } else if (newValue === 3) {
      props.setLoading(true);
      props.getFieldStateAsync(props.accountid, props.idid);
      props.getFieldAsync(props.accountid, props.idid);
    }
    setValue(newValue);
  };

  const [pageState, setPageState] = React.useState(0);
  const pageChange = (event, page) => {
    props.setLoading(true);
    setPageState(Number(page));
    props.getUserAsync(props.accountid, props.idid, props.role, page * ROW_PER_PATE, ROW_PER_PATE);
  };

  const defaultLabelDisplayedRows = ({ from, to, count }) => {
    return `${count !== -1 ? count : `more than ${to}`}件中 ${from}–${to}件目`;
  };

  // ユーザの追加
  const [openUserAdditions, setOpenUserAdditions] = React.useState(false);
  const [userAdditionsObj, setUserAdditionsObj] = React.useState<AdditionUser[]>([INIT_ADDITION_USER]);
  const [userId, setUserId] = React.useState<number>(1);
  const handleAddNewUser = () => {
    const news: AdditionUser[] = JSON.parse(JSON.stringify(userAdditionsObj));
    const newobj: AdditionUser = JSON.parse(JSON.stringify(INIT_ADDITION_USER));
    const newId = userId + 1;
    setUserId(newId);
    newobj.id = newId;
    news.push(newobj);
    setUserAdditionsObj(news);
  }
  const handleUserAdditionsClose = () => {
    setOpenUserAdditions(false);
  };
  const handleAddUser = () => {
    setUserAdditionsObj([INIT_ADDITION_USER]);
    setUserId(1);
    setOpenUserAdditions(true);
  };
  const handleUpdateUsersObject = (users) => {
    setUserAdditionsObj(users);
  };
  const handleOkUserAdditions = () => {
    handleUserAdditionsClose();
    props.setLoading(true);
    props.postUsersAsync(props.accountid, props.idid, props.role, 0, ROW_PER_PATE, props.id, userAdditionsObj);
  }
  const closeCompleteUsersAddtionDialog = () => {
    props.setCompleteUsersAddtion(false);
  };

  // ユーザ更新
  const [openUserUpdate, setOpenUserUpdate] = React.useState(false);
  const [userUpdateObj, setUserUpdateObj] = React.useState<User>(INIT_USER);
  const handleUserUpdateClose = () => {
    setOpenUserUpdate(false);
  };
  const handleUserUpdateOpen = (user) => {
    setUserUpdateObj(user);
    setOpenUserUpdate(true);
  };
  const handleUpdateUserObject = (user) => {
    setUserUpdateObj(user);
  };
  const handleOkUserUpdate = () => {
    props.putUserAsync(props.accountid, props.role, pageState * ROW_PER_PATE, ROW_PER_PATE, userUpdateObj, props.id);
    props.setLoading(true);
    setOpenUserUpdate(false);
    setUserUpdateObj(INIT_USER);
  };

  // ユーザ削除
  const handleDeleteUser = (user: User) => {
    setDeleteUserId(user.userId);
    setSubDialog(true);
  };
  const [deleteUserId, setDeleteUserId] = React.useState('');
  const [subDialog, setSubDialog] = React.useState(false);
  const openSubDialog = Boolean(subDialog);
  const closeSubDialog = () => setSubDialog(false);
  const okSubDialog = () => {
    closeSubDialog();
    props.setLoading(true);
    props.deleteUserAsync(props.accountid, 0, ROW_PER_PATE, props.id, deleteUserId);
  };

  // ユーザグループ設定
  const commitUserGroupsUpdate = (fileName: string) => {
    props.postUserGroupsAsync(props.accountid, props.idid, props.id, fileName);
  };
  const renewUserGroupsState = () => {
    props.setLoading(true);
    props.getUserGroupsStateAsync(props.accountid, props.idid);
  }

  // 体系目次設定
  const commitCatalogUpdate = (fileName: string) => {
    props.postCatalogAsync(props.accountid, props.idid, props.id, fileName);
  };
  const renewCatalogState = () => {
    props.setLoading(true);
    props.getCatalogStateAsync(props.accountid, props.idid);
  }

  // 分野設定
  const commitFieldUpdate = (fileName: string) => {
    props.postFieldAsync(props.accountid, props.idid, props.id, fileName);
  };
  const renewFieldState = () => {
    props.setLoading(true);
    props.getFieldStateAsync(props.accountid, props.idid);
  }

  // 種別設定
  const commitActTypeUpdate = (fileName: string) => {
    props.setLoading(true);
    props.postActTypeAsync(props.accountid, props.idid, props.id, fileName);
  };

  // 表示系
  const createAction = (user: User) => {
    // 自身は更新できない
    if (props.id === user.userId) {
      return '';
    }
    // ユーザが停止状態であれば削除可能とする
    const deleteicon = user.enabled ? '' : (
      <Tooltip title="ユーザーを削除する">
        <IconButton sx={{
          color: (theme) => theme.palette.secondary.main,
          margin: 1,
        }} edge="end" aria-label="edit" onClick={() => handleDeleteUser(user)}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    );

    return (
      <>
        <>
          <Tooltip title="ユーザー情報を更新する">
            <IconButton sx={{
              color: (theme) => theme.palette.secondary.main,
              margin: 1,
            }} edge="end" aria-label="enforcedate" onClick={() => handleUserUpdateOpen(user)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          {deleteicon}
        </>
      </>
    );
  }

  const createUsers = () => {
    const results = props.usersResult.hit.map((r, index) => {
      return (
        <div key={`result-${index}`}>
          <ListItem sx={{
            backgroundColor: (theme) => r.enabled ? theme.palette.primary[50] : theme.palette.secondary[50],
          }} secondaryAction={
            <>
              {createAction(r)}
            </>
          }>
            <ListItemIcon sx={{
              color: (theme) => theme.palette.primary.main,
              margin: 1,
            }}>
              <AccountCircle />
            </ListItemIcon>
            <ListItemText primary={
              <>
                <Typography variant="body1" component="span" sx={{
                  display: 'inline-block',
                  width: '70%',
                  verticalAlign: 'middle'
                }}>
                  {r.name}
                </Typography>
              </>
            } secondary={
              <>
                <Tooltip title="Eメール">
                  <MailIcon color='primary' sx={{
                    position: 'relative',
                    top: 5,
                    left: 5,
                    marginRight: 2,
                  }} />
                </Tooltip>
                <Typography variant="body1" component="span">{r.eMail}</Typography>
                <br />
                <Tooltip title="ユーザー権限">
                  <Group color='primary' sx={{
                    position: 'relative',
                    top: 5,
                    left: 5,
                    marginRight: 2,
                  }} />
                </Tooltip>
                <Typography variant="body1" component="span">{ROLE_NAME[r.role]}</Typography>
                <br />
                <Tooltip title="所属グループ">
                  <AttributionIcon color='primary' sx={{
                    position: 'relative',
                    top: 5,
                    left: 5,
                    marginRight: 2,
                  }} />
                </Tooltip>
                <Typography variant="body1" component="span">{r.userGroupName}</Typography>
                <br />
                <Tooltip title="ユーザーステータス">
                  <InfoIcon color='primary' sx={{
                    position: 'relative',
                    top: 5,
                    left: 5,
                    marginRight: 2,
                  }} />
                </Tooltip>
                <Typography variant="body1" component="span">{r.enabled ? USER_STATUS[r.userStatus] : 'ユーザーは無効化されています。'}</Typography>
              </>
            } />
          </ListItem>
          <Divider />
        </div>
      );
    });

    return (
      <Paper sx={{
        minWidth: 275,
        margin: 1,
        padding: 1,
        backgroundColor: (theme) => theme.palette.primary[50],
      }} elevation={3}>
        <List component="nav" aria-label="My editing acts">
          {results}
        </List>
      </Paper>
    );
  };

  const createTabs = () => {
    const tabs: React.JSX.Element[] = [];
    tabs.push((<Tab key={0} label="ユーザー設定" value={0} {...a11yProps(0)} />));
    if (props.role === 'Manager') {
      tabs.push((<Tab key={1} label="ユーザーグループ設定" value={1} {...a11yProps(1)} />));
      tabs.push((<Tab key={2} label="体系設定" value={2} {...a11yProps(2)} />));
      tabs.push((<Tab key={3} label="分野設定" value={3} {...a11yProps(3)} />));
      tabs.push((<Tab key={4} label="種別設定" value={4} {...a11yProps(4)} />));
    } else if (props.role === 'Approver') {
      tabs.push((<Tab key={2} label="体系設定" value={2} {...a11yProps(2)} />));
      tabs.push((<Tab key={3} label="分野設定" value={3} {...a11yProps(3)} />));
    }

    return tabs;
  };

  return (
    <>
      <Typography sx={{ margin: 2 }} variant="h5" gutterBottom>
        <SettingsIcon fontSize='large' color='primary' sx={{ marginBottom: 1, marginRight: 1, verticalAlign: 'middle' }} />
        環境設定
      </Typography>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
            {createTabs()}
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <Tooltip title="ユーザーを追加する">
            <Fab size="small" color="secondary" aria-label="list" sx={{
              margin: 1,
            }} onClick={handleAddUser}>
              <PersonAddIcon />
            </Fab>
          </Tooltip>
          {createUsers()}
          <TablePagination
            rowsPerPageOptions={[10]}
            component="div"
            count={props.usersResult ? props.usersResult.found : 0}
            rowsPerPage={ROW_PER_PATE}
            page={props.usersResult.start ? props.usersResult.start / ROW_PER_PATE : 0}
            onPageChange={pageChange}
            labelDisplayedRows={defaultLabelDisplayedRows}
          /><br /><br />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <UserGroupsUpdatePanel
            userGroups={props.userGroups}
            userGroupsState={props.userGroupsState}
            accountid={props.accountid}
            idid={props.idid}
            handleCommit={commitUserGroupsUpdate}
            renewUserGroupsState={renewUserGroupsState}
            setLoading={props.setLoading}
          />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <CatalogUpdatePanel
            catalogState={props.catalogState}
            catalogs={props.catalogs}
            maxCatalogId={props.maxCatalogId}
            accountid={props.accountid}
            idid={props.idid}
            role={props.role}
            handleCommit={commitCatalogUpdate}
            renewCatalogState={renewCatalogState}
            renewCatalogs={props.getCatalog}
            setMaxCatalogId={props.setMaxCatalogId}
            setLoading={props.setLoading}
          />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={3}>
          <FieldUpdatePanel
            filedState={props.fieldState}
            fields={props.fields}
            maxFieldId={props.maxFieldId}
            accountid={props.accountid}
            idid={props.idid}
            role={props.role}
            handleCommit={commitFieldUpdate}
            renewFieldState={renewFieldState}
            renewFields={props.getField}
            setMaxFieldId={props.setMaxFieldId}
            setLoading={props.setLoading}
          />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={4}>
          <ActTypeUpdatePanel
            accountid={props.accountid}
            idid={props.idid}
            completeActTypeAddtion={props.completeActTypeAddtion}
            handleCommit={commitActTypeUpdate}
            setCompleteActTypeAddtion={props.setCompleteActTypeAddtion}
            setLoading={props.setLoading}
          />
        </CustomTabPanel>
      </Box>
      <Dialog
        open={props.completeUsersAddtion}
        keepMounted
        onClose={closeCompleteUsersAddtionDialog}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle id="alert-dialog-slide-title">追加処理結果確認</DialogTitle>
        <DialogContent>
          <Typography>{'ユーザーの追加処理が完了しました。'}</Typography>
          <Typography>{'ダウンロードされたファイルから処理結果をご確認ください。'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCompleteUsersAddtionDialog} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openSubDialog}
        keepMounted
        onClose={closeSubDialog}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle id="alert-dialog-slide-title">削除確認</DialogTitle>
        <DialogContent>
          <Typography>{'このユーザーを削除します。よろしいですか？'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={okSubDialog} color="primary">
            OK
          </Button>
          <Button onClick={closeSubDialog} color="primary">
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>
      <UserUpdateDialog
        open={openUserUpdate}
        user={userUpdateObj}
        userGroups={props.userGroups}
        role={props.role}
        handleUpdateUserObject={handleUpdateUserObject}
        handleClose={handleUserUpdateClose}
        handleOk={handleOkUserUpdate}
      />
      <UserAdditionDialog
        open={openUserAdditions}
        users={userAdditionsObj}
        userGroups={props.userGroups}
        role={props.role}
        handleUpdateUsersObject={handleUpdateUsersObject}
        handleAddNewUser={handleAddNewUser}
        handleClose={handleUserAdditionsClose}
        handleOk={handleOkUserAdditions}
      />
    </>
  );

};